import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'

import ManualEntryForm from '../../charity-portal/manual-pad-form'
import { BankAccountSetup } from '../../zumrails-iframe'
import { mutations, queries } from '../../../graphql'
import {
  BlockQuote,
  Button,
  CardBox,
  Collapse,
  Container,
  Fields,
  Icons,
  InputAdornment,
  NotificationDialog,
  Recurring,
  Row,
  Scheduled,
  Stack,
  Text,
  useAlert,
  validations,
} from '../../../components'
import { fromAmountField, getTrackingData } from '../../../utils'

function FAQ() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <BlockQuote sx={{ pt: 1, pb: 1 }}>
      <Row alignItems="center">
        <Button unstyled onClick={() => setIsOpen(!isOpen)}>
          <Text.Bold>FAQs</Text.Bold>
          {isOpen ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
        </Button>
      </Row>
      <Collapse in={isOpen} unmountOnExit sx={{ mt: '0 !important' }}>
        <Text.Body>
          <ul style={{ listStyle: 'none', lineHeight: 2 }}>
            <li>
              <b>Does GiveWise store any banking information?</b> No, GiveWise does not store any banking information.
              GiveWise uses a banking platform called ZumRails which allows you to contribute to your fund directly from
              your linked bank accounts.
            </li>
            <li>
              <b>Why are my contributions showing in &ldquo;Pending&rdquo; state?</b> When you make a contribution
              directly from one of your linked bank accounts, you are beginning the transfer of funds. Banks will take
              some time to verify these transactions. When the transaction is verified by the bank, it will be be marked
              as &ldquo;Cleared&rdquo;, or &ldquo;Cancelled&rdquo; / &ldquo;Failed&rdquo; if the transaction could not
              be verified.
            </li>
            <li>
              <b>Can I use funds that are in the &ldquo;Pending&rdquo; state?</b> No. Once the contribution is marked in
              the &ldquo;Cleared&rdquo; state, the funds will be available in your Giving Wallet, will appear on the Tax
              Receipt, and will be available to gift to charities.
            </li>
            <li>
              <b>How long will my contribution take to clear the bank?</b> Your contribution will take up to 4 business
              days.
            </li>
          </ul>
        </Text.Body>
      </Collapse>
    </BlockQuote>
  )
}

export function BankTransferZum({
  isCharityPortal = false,
  isStepper = false,
  setIsPaymentUILoading = null,
  activeFundId = null,
}) {
  const { fundId: urlFundId } = useParams()
  const fundId = activeFundId ?? (urlFundId ? Number(urlFundId) : null)
  const [openManualOption, setManualOption] = useState(false)
  const [enableManualOption, setEnableManualOption] = useState(false)

  const {
    data: { userHasBankAccount } = {},
    loading: userHasBankAccountLoading,
    refetch: refetchUserHasBankAccount,
  } = useQuery(queries.paymentMethods.userHasBankAccount, {
    variables: { fundId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })

  const { data: { me } = {} } = useQuery(queries.user.me)
  const isCharityUser = (me?.charityUsers || []).length > 0

  const { data: { getZumRailsUser } = {}, refetch: refetchGetZumRailsUser } = useQuery(
    queries.paymentMethods.getZumRailsUser,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  )

  useEffect(() => {
    if (getZumRailsUser && getZumRailsUser !== '') {
      setEnableManualOption(getZumRailsUser)
    } else {
      setEnableManualOption(false)
    }
  }, [getZumRailsUser])

  const handleSetupSuccess = () => {
    refetchUserHasBankAccount()
    setManualOption(false)
  }

  useEffect(() => {
    if (userHasBankAccountLoading || userHasBankAccount === undefined) {
      setIsPaymentUILoading?.(true)
    } else {
      setIsPaymentUILoading?.(false)
    }
  }, [userHasBankAccountLoading, userHasBankAccount, setIsPaymentUILoading])

  const content = () => {
    if (userHasBankAccountLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>One moment...</Text.Body>
          </div>
        </div>
      )
    }

    if (userHasBankAccount === undefined) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>Loading bank account information...</Text.Body>
          </div>
        </div>
      )
    }

    if (userHasBankAccount) {
      return <BankTransferForm isStepper={isStepper} activeFundId={fundId} />
    }

    return (
      <>
        <BankAccountSetup
          setEnableManualOption={setEnableManualOption}
          refetchGetZumRailsUser={refetchGetZumRailsUser}
          setIsPaymentUILoading={setIsPaymentUILoading}
          hideText={isStepper}
          activeFundId={fundId}
        />
        {enableManualOption && !isStepper && isCharityUser && (
          <Stack>
            <Text.Body>
              We recognize that not all banks are available for aggregation. If you are experiencing issues connecting
              to your bank using your online banking credentials, you can manually enter your banking information.
              Please ensure that all details are entered accurately to avoid any delays or errors.
            </Text.Body>
            <Box sx={{ mb: 1 }}>
              <Button variant="contained" onClick={() => setManualOption(true)}>
                Manual Entry
              </Button>
            </Box>
          </Stack>
        )}
      </>
    )
  }

  // If it's being rendered from CharityPortal, skip the extra container/cardbox
  if (isCharityPortal) {
    return <Stack spacing={4}>{content()}</Stack>
  }

  return isStepper ? (
    <Stack spacing={4}>
      {content()}
      <FAQ />
    </Stack>
  ) : (
    <Container maxWidth="lg">
      <CardBox title="Bank Transfer">
        <Stack spacing={4}>
          <FAQ />
          {openManualOption ? (
            <CardBox sx={{ my: 3 }}>
              <ManualEntryForm onSuccess={handleSetupSuccess} />
            </CardBox>
          ) : (
            content()
          )}
        </Stack>
      </CardBox>
    </Container>
  )
}

function BankTransferForm({ isStepper = false, activeFundId = null }) {
  const { fundId: urlFundId } = useParams()
  const fundId = activeFundId ?? (urlFundId ? Number(urlFundId) : null)
  const { data: { me: { fund } = { fund: {} } } = {} } = useQuery(queries.funds.myFund)
  const { data: userData } = useQuery(queries.user.me)
  const profile = userData?.me?.profile || {}
  const isCharityUser = (userData?.me?.charityUsers || []).length > 0
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const { data: { linkedZumRailsBanks = [] } = {}, loading: loadingZumRailsBanks } = useQuery(
    queries.paymentMethods.linkedZumRailsBanks,
    {
      variables: { fundId },
      fetchPolicy: 'network-only',
    }
  )
  const {
    control,
    register,
    handleSubmit,
    formState,
    setValue,
    getValues,
    reset: resetForm,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: 0,
      isRecurring: false,
      recurringInterval: 'monthly',
    },
  })
  const closeSuccessDialog = () => {
    setDialogOpen(false)
    resetForm()
  }
  const [createContribution, _] = useMutation(mutations.contributions.createContribution, {
    refetchQueries: [
      { query: queries.funds.myFundActivity, variables: { fundId: Number(fundId) } },
      { query: queries.paymentMethods.linkedZumRailsBanks },
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setIsSubmitting(false)
      setDialogOpen(true)
    },
    onError: (e) => {
      setIsSubmitting(false)
      setAlert({
        message: `Failed to create a contribution: ${e.message ?? ''}`,
        error: e.message,
        severity: 'error',
      })
      resetForm()
    },
  })
  const { errors = {} } = formState
  const onSubmit = async ({ amount, isRecurring, recurringInterval, processingDate }) => {
    setIsSubmitting(true)
    const contributionObj = {
      paymentMethodId: Number(linkedZumRailsBanks?.[0]?.PaymentMethodId),
      fundId: Number(fund.id),
      amount: fromAmountField(amount),
      isRecurring,
      recurringInterval,
      processingDate,
      processor: 'zumrails',
      ...getTrackingData(),
    }

    await createContribution({
      variables: {
        data: contributionObj,
      },
    })
  }

  const { Institution, AccountNumber } = linkedZumRailsBanks.length > 0 ? linkedZumRailsBanks[0] : {}

  const bankAccountInfo = () => {
    const loaderOrInfo = () => {
      if (loadingZumRailsBanks) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: '8px',
            }}
          >
            <CircularProgress size={16} color="info" />
          </div>
        )
      }

      return (
        <Text.Body style={{ marginLeft: '8px', color: 'grey' }}>
          {`${Institution} - **** ${AccountNumber?.slice(-4)}`}
        </Text.Body>
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          padding: '8px',
        }}
      >
        <Icons.Account />
        <Text.Body style={{ marginLeft: '8px' }}>Bank Account</Text.Body>
        {loaderOrInfo()}
      </div>
    )
  }

  return isStepper ? (
    <>
      {bankAccountInfo()}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="text" to={`/profile/${fundId}/payment-methods`} style={{ marginTop: 0 }}>
          Manage Bank Accounts
        </Button>
      </Box>
    </>
  ) : (
    <>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <Stack spacing={2}>
          <Fields.Text
            label="Amount"
            name="amount"
            error={!!errors?.amount}
            helperText={errors?.amount?.message}
            InputProps={{
              inputComponent: Fields.Amount,
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: {
                setValue: (value) => setValue('amount', value),
                getValue: () => getValues('amount'),
                defaultValue: 0,
              },
            }}
            {...register('amount', {
              ...{ min: { value: 1, message: 'Must be at least 1' } },
              ...validations.required,
              ...validations.number,
            })}
          />
          {bankAccountInfo()}

          <Scheduled register={register} errors={errors} control={control} getValues={getValues} setValue={setValue} />
          <Recurring register={register} errors={errors} getValues={getValues} setValue={setValue} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="text" to={`/profile/${fundId}/payment-methods`} style={{ marginTop: 0 }}>
              Manage Bank Accounts
            </Button>
          </Box>

          <Button type="submit" sx={{ alignSelf: 'flex-end' }} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
          <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
        </Stack>
      </form>
      <NotificationDialog
        open={dialogOpen}
        onClose={closeSuccessDialog}
        type="success"
        title="Success!"
        message="You have successfully created a contribution."
        actions={[
          {
            label: 'Done',
            onClick: closeSuccessDialog,
            to: isCharityUser ? undefined : `/funds/${fund.id}/activity/giving-wallet`,
          },
        ]}
      />
    </>
  )
}
